// ── logs-and-exports: the Wave-10 module-access + export≤view release-gate logic ─
// Logs/History · Exports · Settings/Thresholds · Tool View.
//
// CONTRACT TRUTH (read from apps/vjal-coaching-api/src — authoritative):
//   1. LOGS/HISTORY — GET /event-history (+ /event-history/:event_id) is
//      STAFF-ONLY (the route resolves the read scope and 403s unless
//      scope.mode === 'all'; resolveGlobalObservabilityRead). There are NO
//      dedicated invite / report-generation / participant / session log routes;
//      the "distinct logs" the PRD names are FILTERED VIEWS over event-history
//      (the taxonomy carries the categories: foundation/participant/session/
//      integration[invite,zoom]/report/transcript/resource/permission/user/
//      profile/platform_event/migration). So we present them as scoped views over
//      the one real read — never a fabricated second log source.
//   2. EXPORTS — GET /exports/:export_type returns text/csv for NINE types
//      (companies, programs, cohorts, participants, sessions, reports,
//      transcripts, resources, event-history). Each has a per-type VIEW
//      permission; event-history is staff-only; the reports CSV is D-3
//      visibility-filtered; transcripts are metadata-only (no raw transcript
//      text). The backend does ONLY CSV — there is NO Excel/Word/PDF/PPT export.
//   3. SETTINGS/THRESHOLDS — GET/POST /settings/thresholds is LIVE through the
//      readiness settings client. Missing rows remain honestly "not set"; a
//      denied or unavailable read is rendered as that runtime posture.
//   4. TOOL VIEW — GET /tools/diagnostics is LIVE through the readiness tools
//      client and is super-admin-only. It exposes states/counts/booleans only;
//      permission and outage responses remain honest postures.

//
// ── RELEASE GATE: EXPORT ≤ VIEW (FR-PERM-002), scope frozen at request ───────
// The set of export types a role may SEE and DOWNLOAD must NEVER exceed its view
// scope. This is a fail-closed allow-list keyed on each export type's view
// surface. `exportTypesForRole` is the single source of truth the Exports page
// renders from AND the assertion target the biting tests use; widening it past a
// role's view scope must fail the gate test.

import type { CoachingRole } from "./roles";
import { isStaffRole } from "./sensitive-surfaces";

// ── The 9 real CSV export types (backend GET /exports/:export_type) ──────────
export const COACHING_EXPORT_TYPES = [
  "companies",
  "programs",
  "cohorts",
  "participants",
  "sessions",
  "reports",
  "transcripts",
  "resources",
  "event-history"
] as const;

export type CoachingExportType = (typeof COACHING_EXPORT_TYPES)[number];

// ── Non-CSV formats the PRD asks for but the backend does NOT serve ──────────
// Trust source: only CSV is real. The other formats render as an honest
// "not available (CSV only for now)" — never a fabricated download.
export const NON_CSV_EXPORT_FORMATS = ["excel", "word", "pdf", "pptx"] as const;
export type NonCsvExportFormat = (typeof NON_CSV_EXPORT_FORMATS)[number];

export const NON_CSV_FORMAT_LABEL: Record<NonCsvExportFormat, string> = {
  excel: "Excel (.xlsx)",
  word: "Word (.docx)",
  pdf: "PDF",
  pptx: "PowerPoint (.pptx)"
};

// ── per-export-type VIEW surface (mirrors the backend exportPermission map) ──
// Each export type is gated on the SAME view permission as its list read, so the
// export type list a role sees can never exceed what it can view. event-history
// is STAFF-ONLY (the backend 403s a non-'all' scope). Reports/transcripts are
// admitted by view scope here; the row-level D-3 visibility filter (applied on
// the reports list + the backend reports CSV) keeps confidential tiers out — so
// "view" for reports means "may view the reports surface", not "every report".
type ExportTypeRule = {
  type: CoachingExportType;
  /** Roles whose VIEW scope includes this export type's source surface. */
  viewRoles: readonly CoachingRole[];
};

const STAFF: readonly CoachingRole[] = ["super_admin", "admin"];
const STAFF_COACH: readonly CoachingRole[] = ["super_admin", "admin", "coach"];
// company_admin + cohort_lead are view-only assigned-scope observers: they may
// view (and so export, ≤ their view) the cohort/participant/session/report
// surfaces they can read, but never the staff-only event-history audit log.
const STAFF_COACH_CO: readonly CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "cohort_lead"];

const EXPORT_TYPE_RULES: ExportTypeRule[] = [
  { type: "companies", viewRoles: ["super_admin", "admin", "coach", "company_admin"] },
  { type: "programs", viewRoles: ["super_admin", "admin", "coach", "company_admin"] },
  { type: "cohorts", viewRoles: STAFF_COACH_CO },
  { type: "participants", viewRoles: STAFF_COACH_CO },
  { type: "sessions", viewRoles: STAFF_COACH_CO },
  { type: "reports", viewRoles: STAFF_COACH_CO },
  { type: "transcripts", viewRoles: STAFF_COACH }, // transcript reads hard-403 cohort_lead + participant
  { type: "resources", viewRoles: STAFF_COACH_CO },
  { type: "event-history", viewRoles: STAFF } // staff-only audit log (backend mode==='all')
];

const RULE_BY_TYPE = new Map<CoachingExportType, ExportTypeRule>(EXPORT_TYPE_RULES.map((rule) => [rule.type, rule]));

/**
 * The EXACT export types a role may see + download (export ≤ view, fail-closed).
 * A participant is ALWAYS empty (denied the Exports module entirely). The order
 * is the canonical COACHING_EXPORT_TYPES order. This is the single source of
 * truth for the rendered chooser AND the gate assertion.
 */
export function exportTypesForRole(role: CoachingRole): CoachingExportType[] {
  if (role === "participant") return [];
  return COACHING_EXPORT_TYPES.filter((type) => {
    const rule = RULE_BY_TYPE.get(type);
    return rule ? rule.viewRoles.includes(role) : false;
  });
}

/** Whether a role may select/download a specific export type (export ≤ view). */
export function canExportType(role: CoachingRole, type: CoachingExportType): boolean {
  if (role === "participant") return false;
  const rule = RULE_BY_TYPE.get(type);
  return rule ? rule.viewRoles.includes(role) : false;
}

// ── Exports MODULE access (the Exports route/page allow-list) ────────────────
// Bulk CSV export is for STAFF / coach / company_admin / cohort_lead — i.e.
// anyone with at least one exportable view surface. PARTICIPANT is DENIED the
// module (route + nav + page fail-closed). cohort_lead/company_admin reach the
// module but see ONLY their authorized (≤ view) export types.
export const EXPORTS_MODULE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

export function canAccessExportsModule(role: CoachingRole): boolean {
  return EXPORTS_MODULE_ROLES.includes(role) && exportTypesForRole(role).length > 0;
}

// ── Logs/History MODULE access (staff + scoped operational roles) ────────────
// The backend returns org-wide history for staff and scope-filtered history for
// coach/company_admin/cohort_lead. PARTICIPANT remains denied the module.
export const LOGS_MODULE_ROLES: readonly CoachingRole[] = ["super_admin", "admin", "coach", "company_admin", "cohort_lead"];

export function canAccessLogsModule(role: CoachingRole): boolean {
  return LOGS_MODULE_ROLES.includes(role);
}

/** Whether the role may read the unfiltered org-wide audit table. */
export function canReadAuditLog(role: CoachingRole): boolean {
  return isStaffRole(role);
}

// ── Settings/Thresholds MODULE access ────────────────────────────────────────
// The live readiness settings client reads GET /settings/thresholds. The admin
// Settings/Thresholds surface remains STAFF/coach-where-allowed and renders
// absent rows as "not set"; participant access stays denied.
export const SETTINGS_ADMIN_ROLES: readonly CoachingRole[] = ["super_admin", "admin", "coach"];

export function canAccessThresholdSettings(role: CoachingRole): boolean {
  return SETTINGS_ADMIN_ROLES.includes(role);
}

/** Whether the role may edit thresholds through the live POST endpoint. */
export function canEditThresholds(role: CoachingRole): boolean {
  return role === "super_admin" || role === "admin";
}

// ── Tool View access (FR-TOOL-001 — live diagnostics, SA-only) ───────────────
// The readiness tools client reads GET /tools/diagnostics. The endpoint is
// read-only and secret-safe; everyone except super_admin is denied.
export const TOOL_VIEW_ROLES: readonly CoachingRole[] = ["super_admin"];

export function canAccessToolView(role: CoachingRole): boolean {
  return TOOL_VIEW_ROLES.includes(role);
}
